<template>
  <el-dialog
    title="Send progress note to PointClickCare"
    :visible="visible"
    size="small"
    :close-on-click-modal="false"
    :show-close="false"
  >
    <el-form label-position="top" v-loading="busy">
      <el-form-item label="Note type">
        <el-input :value="noteTypeLabel" disabled data-testid="pcc-note-type" />
      </el-form-item>
      <el-form-item label="Date of service">
        <el-date-picker
          v-model="serviceDate"
          type="datetime"
          :format="serviceDateFormat"
          :picker-options="serviceDatePickerOptions"
          :clearable="false"
          data-testid="pcc-note-service-date"
        />
      </el-form-item>
      <el-form-item label="Designation" required>
        <el-select
          v-model="designation"
          placeholder="Select or type a designation"
          filterable
          allow-create
          default-first-option
          :disabled="unconfirmed"
          data-testid="pcc-note-designation"
          style="width: 100%"
        >
          <el-option v-for="d in designationOptions" :key="d" :label="d" :value="d" />
        </el-select>
      </el-form-item>
      <el-form-item :label="sectionName">
        <el-input type="textarea" v-model="noteText" :autosize="{ minRows: 8, maxRows: 18 }" :disabled="unconfirmed" data-testid="pcc-note-text" />
      </el-form-item>
      <p v-if="errorText" class="error">{{ errorText }}</p>
    </el-form>
    <span slot="footer">
      <el-button data-testid="pcc-note-dismiss" @click="dismiss" :disabled="sending">Don't send</el-button>
      <el-button v-if="loadError" data-testid="pcc-note-retry" @click="loadNoteTypes" :disabled="busy">Retry</el-button>
      <el-button data-testid="pcc-note-send" type="primary" @click="send" :loading="sending" :disabled="!canSend">Send to PointClickCare</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { buildInfectionNoteText } from "../../services/infectionNoteText";
import moment from "moment";
import { fetchNoteTypes, findInfectionNoteType, INFECTION_NOTE_TYPE, sendProgressNote, describeSendResult, describeHttpError, describeSendHttpError, isUnconfirmedSendResult, isUnconfirmedSendError, DEFAULT_SECTION_NAME, DESIGNATION_OPTIONS, DESIGNATION_MAX_LENGTH, SendStatus } from "../../services/PccProgressNoteService";

const MISSING_TYPE_MESSAGE = `${INFECTION_NOTE_TYPE} isn't set up for this facility in PointClickCare.`;
const SERVICE_TIME_FORMAT = "HH:mm";
const FUTURE_SERVICE_DATE_MESSAGE = "Date of service can't be in the future.";
const UNCONFIRMED_DISMISS_MESSAGE = "This note may already be in PointClickCare. Close without sending it again?";
const UNCONFIRMED_DISMISS_TITLE = "Warning";
const UNCONFIRMED_DISMISS_CONFIRM = "Close";
const UNCONFIRMED_DISMISS_CANCEL = "Cancel";
const WARNING_TYPE = "warning";

export default {
  name: "SendProgressNoteDialog",
  props: {
    visible: { type: Boolean, required: true },
    client: { type: Object, required: true },        // the case's client (id, firstName, lastName, precautionDetails)
    infectionCase: { type: Object, required: true }, // the created case as returned by the infection API
    caseId: { type: Number, required: true },
    precautionsLookup: { type: Object, default: () => ({}) },
    initialText: { type: String, default: null } // replaces the generated note text when set
  },
  data() {
    return {
      noteTypes: [],
      noteText: "",
      serviceDate: null, // sent to PCC as the note's effectiveDate; set in resetForm()
      defaultServiceDate: null, // still untouched at Send → send-time "now" instead
      serviceDatePickerOptions: {
        // gets calendar cells and typed values alike, so compare the real value with now
        disabledDate(input) {
          return moment(input).isAfter(moment());
        }
      },
      designation: "",
      designationOptions: DESIGNATION_OPTIONS,
      busy: false,
      sending: false,
      loadError: null,
      missingType: false,
      // the last send may have reached PCC: note text and designation stay locked so a resend matches it exactly
      unconfirmed: false
    };
  },
  computed: {
    selectedType() {
      return findInfectionNoteType(this.noteTypes);
    },
    noteTypeLabel() {
      return INFECTION_NOTE_TYPE;
    },
    errorText() {
      return this.loadError || (this.missingType ? MISSING_TYPE_MESSAGE : null);
    },
    sectionName() {
      const sections = (this.selectedType && this.selectedType.sections) || [];
      return sections.length ? sections[0] : DEFAULT_SECTION_NAME;
    },
    serviceDateFormat() {
      return `${this.$configStore.datePickerDateFormat()} ${SERVICE_TIME_FORMAT}`;
    },
    designationValid() {
      const length = this.designation.trim().length;
      return length > 0 && length <= DESIGNATION_MAX_LENGTH;
    },
    canSend() {
      return !!this.selectedType && this.noteText.trim().length > 0 && !!this.serviceDate && this.designationValid && !this.busy;
    }
  },
  watch: {
    // immediate: the parent mounts this component (v-if) with visible already true, so a plain watcher never fires
    visible: {
      immediate: true,
      handler(open) {
        if (open) {
          this.resetForm();
          this.loadNoteTypes();
        }
      }
    }
  },
  methods: {
    // on open only — Retry must keep the user's date of service, designation and note edits
    resetForm() {
      this.defaultServiceDate = new Date();
      this.serviceDate = this.defaultServiceDate;
      this.designation = "";
      this.unconfirmed = false;
      this.noteText = this.initialText !== null
        ? this.initialText
        : buildInfectionNoteText({
          client: this.client,
          infectionCase: this.infectionCase,
          precautionsLookup: this.precautionsLookup,
          dateFormat: this.$configStore.dateFormat()
        });
    },
    async loadNoteTypes() {
      this.busy = true;
      this.loadError = null;
      this.missingType = false;
      try {
        this.noteTypes = await fetchNoteTypes(this.$http, this.client.id);
        this.missingType = !this.selectedType;
      } catch (response) {
        this.loadError = describeHttpError(response).message;
      } finally {
        this.busy = false;
      }
    },
    async send() {
      // the time spinner ignores disabledDate, so a later time today can still be picked
      const effectiveDate = this.serviceDate === this.defaultServiceDate ? new Date() : this.serviceDate;
      if (moment(effectiveDate).isAfter(moment())) {
        this.$message({ type: "error", message: FUTURE_SERVICE_DATE_MESSAGE, duration: 8000 });
        return;
      }
      this.sending = true;
      try {
        const result = await sendProgressNote(this.$http, {
          clientId: this.client.id,
          sourceId: this.caseId,
          noteType: this.selectedType.noteType,
          sections: [{ name: this.sectionName, value: this.noteText.trim() }],
          effectiveDate: effectiveDate.toISOString(),
          designation: this.designation.trim()
        });
        if (isUnconfirmedSendResult(result)) {
          this.unconfirmed = true;
        }
        const { type, message } = describeSendResult(result);
        this.$message({ type, message, duration: type === "success" ? 4000 : 8000 });
        // a failed send keeps the dialog open so the user can retry
        if (result && result.status === SendStatus.Posted) {
          this.$emit("closed", { sent: true });
        }
      } catch (response) {
        if (isUnconfirmedSendError(response)) {
          this.unconfirmed = true;
        }
        const { type, message } = describeSendHttpError(response);
        this.$message({ type, message, duration: 8000 });
      } finally {
        this.sending = false;
      }
    },
    dismiss() {
      if (!this.unconfirmed) {
        this.$emit("closed", { sent: false });
        return;
      }
      this.$confirm(UNCONFIRMED_DISMISS_MESSAGE, UNCONFIRMED_DISMISS_TITLE, {
        confirmButtonText: UNCONFIRMED_DISMISS_CONFIRM,
        cancelButtonText: UNCONFIRMED_DISMISS_CANCEL,
        type: WARNING_TYPE
      }).then(
        () => this.$emit("closed", { sent: false }),
        () => undefined // Cancel keeps the dialog open
      );
    }
  }
};
</script>

<style scoped>
.error { color: #f56c6c; margin: 4px 0 0; }
</style>
